import { useState, useEffect, useContext } from 'react';
import { profileAPI, ProfileData, SocialLink, ContactInfo } from '../services/api';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const useProfile = () => {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useContext(AuthContext);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      
      try {
        // First check the debug endpoint to see if profiles exist
        try {
          const debugResponse = await axios.get<any>(`${API_URL}/profile/debug`);
          console.log('Profile debug response:', debugResponse.data);
        } catch (debugErr) {
          console.error('Debug endpoint error:', debugErr);
        }
        
        // Try to get the profile depending on auth state
        const token = localStorage.getItem('token');
        console.log('Auth token:', token ? 'exists' : 'missing');
        
        let response;
        
        if (currentUser) {
          // If user is logged in, try the authenticated endpoint
          try {
            response = await profileAPI.getProfile();
            console.log('Profile API response from authenticated endpoint:', response);
          } catch (mainErr) {
            console.log('Main profile endpoint failed, trying fallbacks', mainErr);
            
            try {
              // Try debug endpoint
              const debugResponse = await axios.get<any>(`${API_URL}/profile/debug`);
              console.log('Debug endpoint response:', debugResponse.data);
            } catch (debugErr) {
              console.log('Debug endpoint also failed');
            }
            
            // As a last resort, try the direct admin endpoint
            try {
              const adminResponse = await axios.get<any>(`${API_URL}/profile/admin`);
              console.log('Admin endpoint response:', adminResponse.data);
              // If we get here, use this as our response
              response = adminResponse.data;
            } catch (adminErr) {
              console.log('All endpoints failed');
              throw mainErr; // Re-throw the original error
            }
          }
        } else {
          // Not authenticated, try directly with the admin endpoint
          try {
            const adminResponse = await axios.get<any>(`${API_URL}/profile/admin`);
            console.log('Admin endpoint response for public view:', adminResponse.data);
            response = adminResponse.data;
          } catch (adminErr) {
            console.log('Admin endpoint failed for public view', adminErr);
            // Set a more descriptive error but continue execution instead of throwing
            setError('Failed to load admin profile data');
            setLoading(false);
            return; // Exit the function early instead of throwing
          }
        }
        
        // Look at the response structure to verify it
        console.log('Full profile response:', JSON.stringify(response));
        
        if (response) {
          // It might be { success: true, data: profileData } or just the profileData directly
          if (response.success && response.data) {
            console.log('Setting profile data from success/data format:', response.data);
            setProfile(response.data);
            setError(null);
          } else if (response._id) {
            // Direct profile object instead of wrapped
            console.log('Setting profile data directly:', response);
            setProfile(response);
            setError(null);
          } else {
            console.error('Invalid profile response structure:', response);
            setError('Failed to load profile data - Invalid structure');
          }
        } else {
          console.error('Empty profile response');
          setError('Failed to load profile data - Empty response');
        }
        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching profile:', err);
        setError(`Failed to load profile: ${err?.message || 'Unknown error'}`);
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const updateBio = async (bio: string) => {
    try {
      await profileAPI.updateProfile({ bio });
      setProfile(prev => prev ? { ...prev, bio } : null);
      return { success: true };
    } catch (err: any) {
      console.error('Error updating bio:', err);
      return { 
        success: false, 
        error: 'Failed to update bio' 
      };
    }
  };

  const updateSocialLinks = async (socialLinks: SocialLink[]) => {
    try {
      await profileAPI.updateProfile({ socialLinks });
      setProfile(prev => prev ? { ...prev, socialLinks } : null);
      return { success: true };
    } catch (err: any) {
      console.error('Error updating social links:', err);
      return { 
        success: false, 
        error: 'Failed to update social links' 
      };
    }
  };

  const updateContactInfo = async (contactInfo: ContactInfo) => {
    try {
      await profileAPI.updateProfile({ contactInfo });
      setProfile(prev => prev ? { ...prev, contactInfo } : null);
      return { success: true };
    } catch (err: any) {
      console.error('Error updating contact info:', err);
      return { 
        success: false, 
        error: 'Failed to update contact info' 
      };
    }
  };

  return { 
    profile, 
    loading, 
    error,
    updateBio,
    updateSocialLinks,
    updateContactInfo
  };
};

export default useProfile;